import { Modal, Stack, Card, Box, IconButton, FormControl } from '@mui/material';
import { Close } from 'mdi-material-ui';
import { Controller, useForm } from 'react-hook-form';
import { VigoButton } from 'src/@vigo/vigo-button';
import { VigoMultiLine } from 'src/@vigo/vigo-multiline';
import { VigoTextInput } from 'src/@vigo/vigo-textinput';
import { EmailMessageModel, MessagingServiceDto } from 'src/models/messaging';
import { useAuth } from 'react-oidc-context';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMessageTypes, getMessagingServices, sendEmailMessage } from 'src/services/messaging';
import * as yup from 'yup';
import { memo, useCallback } from 'react';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import useImpersonateSessionData from 'src/@core/hooks/useImpersonateSessionData';
import { yupResolver } from '@hookform/resolvers/yup';

interface EmailComposerProps {
  open: boolean;
  handleClose: () => void;
  messagingService?: MessagingServiceDto;
  orderId: string;
  consignmentNumber: string;
}

const schemaResolver = yup.object({
  emailAddress: yup.string().required('Email address is required').email('Invalid email address')
});

const EmailComposer = ({ open, handleClose, messagingService, orderId, consignmentNumber }: EmailComposerProps) => {
  // Hooks
  const { vigoToast } = useVigoToast({});
  const methods = useForm({
    resolver: yupResolver(schemaResolver)
  });
  const { control, setValue: setFormValue } = methods;
  const { isImpersonating, impersonateData } = useImpersonateSessionData();

  const auth = useAuth();
  const user = auth.user;
  let subdomain = '';

  // check if we are already impersonating a tenant
  if (isImpersonating) {
    subdomain = impersonateData?.subdomain ?? '';
  } else {
    subdomain = user?.profile?.subdomain as string;
  }
  const messagingServiceScope = isImpersonating
    ? impersonateData.partnerId || impersonateData.tenantId || subdomain
    : (user?.profile?.partnerId as string) || subdomain;

  const iss = user?.profile.iss as string;
  // get the environment from the iss between the first two dots
  const env = iss.split('.')[1] ?? 'vigocloud';
  const comOrNet = iss.split('.')[2] ?? 'com';

  const trackingLink = `https://${subdomain}.myportal.${env}.${comOrNet}/tracking/?id=${orderId}`;
  const emailSubject = `Tracking Link for Consignment: ${consignmentNumber ?? ''}`;
  const emailMessage = `To track consignment ${consignmentNumber ?? ''}, please follow the link: ${trackingLink}`;

  const { data: deferredMessagingService, isFetching: messagingServiceIsFetching } = useQuery({
    queryKey: ['defaultEmailMessagingService', messagingServiceScope],
    queryFn: async () => {
      const messageTypes = (await getMessageTypes({ filter: 'isEmail eq true' })).data;
      const emailMessageTypeId = messageTypes?.[0]?.id;

      if (!emailMessageTypeId) return null;

      const messagingServices = (
        await getMessagingServices({
          filter: `messageTypeId eq guid'${emailMessageTypeId}' and isDefault eq true`
        })
      ).data;

      return messagingServices?.[0];
    },
    enabled: open && !messagingService,
    refetchOnWindowFocus: false,
    onError: () => vigoToast.error('Unable to load the email service configuration')
  });
  const resolvedMessagingService = messagingService ?? deferredMessagingService;

  const sendEmailMutation = useMutation({
    mutationFn: async (obj: EmailMessageModel) => {
      await sendEmailMessage(obj);
    }
  });

  const onSubmit = useCallback(() => {
    methods.handleSubmit((formData: any) => {
      const email: EmailMessageModel = {
        messageBody: emailMessage,
        messagingServiceId: resolvedMessagingService?.id,
        purpose: 'email pod',
        subject: emailSubject,
        to: [formData.emailAddress]
      };

      return vigoToast.promise(
        'Sending email',
        'Email sent',
        'Error sending email',
        false,
        sendEmailMutation.mutateAsync(email, {
          onSuccess: () => {
            setFormValue('emailAddress', undefined);

            handleClose();
          }
        })
      );
    })();
  }, [emailMessage, emailSubject, handleClose, methods, resolvedMessagingService, sendEmailMutation, setFormValue, vigoToast]);

  const onClose = useCallback(() => {
    setFormValue('emailAddress', undefined);

    handleClose();
  }, [handleClose, setFormValue]);

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Card sx={{ width: 'min(700px, 95%)' }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#626679',
              padding: '10px'
            }}>
            <VigoTypography
              sx={{ color: '#fff', fontSize: '1.143rem', fontWeight: 600 }}
              value="Email Consignment Tracking Link"
            />
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: '#fff', '&:hover': { borderRadius: '4px', backgroundColor: '#fff', color: '#626679' } }}>
              <Close />
            </IconButton>
          </Box>

          <form onSubmit={e => e.preventDefault()}>
            <Stack spacing={4} padding="15px">
              {/* Email Address */}
              <FormControl sx={{ width: '100%' }}>
                <Controller
                  control={control}
                  name="emailAddress"
                  render={({ field, fieldState: { error } }) => (
                    <VigoTextInput
                      {...field}
                      required
                      label="Email Address"
                      value={field.value || ''}
                      error={!!error}
                      errormessage={error?.message}
                    />
                  )}
                />
              </FormControl>

              {/* Email Subject */}
              <VigoTextInput
                name="emailSubject"
                fullWidth
                inputProps={{ readOnly: true }}
                label="Email Subject"
                value={emailSubject}
              />

              {/* Message */}
              <VigoMultiLine name="emailMessage" fullWidth label="Message" minRows={6} readOnly value={emailMessage} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                <VigoButton
                  variant="contained"
                  disableTranslation
                  onClick={onClose}
                  disabled={sendEmailMutation.isLoading}
                  type="button"
                  name="cancel"
                  value="cancel"
                  sx={{
                    color: '#626679',
                    textTransform: 'capitalize',
                    backgroundColor: '#fff',
                    minWidth: '100px',
                    boxShadow: 'none',
                    fontSize: '1rem',
                    '&:hover': { color: '#fff', backgroundColor: '#626679' }
                  }}
                />
                <VigoButton
                  variant="contained"
                  disableTranslation
                  disabled={sendEmailMutation.isLoading || messagingServiceIsFetching || !resolvedMessagingService?.id}
                  type="submit"
                  onClick={onSubmit}
                  name="send"
                  value="send"
                  sx={{
                    color: '#fff',
                    textTransform: 'capitalize',
                    backgroundColor: '#626679',
                    minWidth: '100px',
                    fontSize: '1rem'
                  }}
                />
              </Box>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Modal>
  );
};

export default memo(EmailComposer);
